import { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useUpdatePropertyMutation } from '../store/docApi';
import Notes from '../components/widgets/Notes';
import { getReferencesHtml, NoteReferences } from './NoteReferences';
import Markdown from '../components/widgets/Markdown';
import TiptapEditor, { markdownToHtml } from './TipTapEditor';

const QStep = ({ step, depth = 0, onAnswerChange, answers, bibliography, updateReferences }) => {
  const [answer, setAnswer] = useState((answers[step.id] && answers[step.id].note) || '');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setAnswer((answers[step.id] && answers[step.id].note) || '');
  }, [answers, step.id]);

  return (
    !step.noquestions && (
      <Box sx={{ mb: 1 }}>
        {step.title && <Typography variant="h6" gutterBottom>{step.title}</Typography>}
        {!step.question && step.name && (
          <Typography variant="subtitle1" gutterBottom>
            <b>{step.name}</b>
          </Typography>
        )}
        {step.tags && (
          <Box sx={{ mb: 1 }}>
            {step.tags.map((tag, i) => (
              <Chip key={i} variant="outlined" color="success" size="small" sx={{ mr: 0.5 }} label={tag} />
            ))}
          </Box>
        )}
        {step.notes && (
          <Box sx={{ mb: 1 }}>
            <Notes notes={step.notes} />
          </Box>
        )}
        {step.question && (
          <>
            {!step.verbal && (
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                <b>{step.name}</b> {step.question}
              </Typography>
            )}
            <Box sx={{ width: '100%', mb: 2 }}>
              {!isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box 
                    sx={{
                      flex: 1,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      minHeight: 60,
                      cursor: !answer ? 'pointer' : 'default',
                      width: '100%'
                    }}
                    onClick={() => !answer && setIsEditing(true)}
                  >
                    <Markdown text={answer || 'Click to add answer...'} />
                  </Box>
                  <IconButton size="small" onClick={() => setIsEditing(true)}>
                    <EditIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <TiptapEditor content={answer} setContent={setAnswer} />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {isEditing && (
                <>
                  <Button size="small" variant="contained" onClick={() => {
                      setIsEditing(false);
                      onAnswerChange(step.id, answer);
                    }}>
                    Save
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => {
                      setIsEditing(false);
                      setAnswer((answers[step.id] && answers[step.id].note) || '');
                    }}>
                    Cancel
                  </Button>
                </>
              )}
            </Box>
            <NoteReferences
              references={(answers[step.id] && answers[step.id].references) || []}
              referencesId={step.id}
              bibliography={bibliography}
              updateReferences={updateReferences}
            />
          </>
        )}
        {step.endnote && <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>{step.endnote}</Typography>}
        <Divider/>
        {step.steps && step.steps.length > 0 && (
          <Box sx={{ pl: 2, ml: 1, mt: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
            {step.steps.map((subStep, i) => (
              <QStep
                key={i}
                step={subStep}
                depth={depth + 1}
                onAnswerChange={onAnswerChange}
                answers={answers}
                bibliography={bibliography}
                updateReferences={updateReferences}
              />
            ))}
          </Box>
        )}
      </Box>
    )
  );
};

const Questions = ({ item, connectionIndex, currentStep }) => {
  const [answers, setAnswers] = useState({});
  const [updateProperty] = useUpdatePropertyMutation();
  const [bibliography, setBibliography] = useState([]);

  useEffect(() => {
    if (item) {
      if (item.connections && item.connections.length > connectionIndex) {
        let connection = item.connections[connectionIndex];
        setAnswers(connection.answers ? JSON.parse(JSON.stringify(connection.answers)) : {});
      }
      if (item.bibliography) {
        setBibliography(item.bibliography);
      }
    }
  }, [item, connectionIndex]);

  async function handleAnswerChange(id, value) {
    let newanswers = { ...answers };
    if (!newanswers[id]) {
      newanswers[id] = {};
    }
    newanswers[id].note = value;
    setAnswers(newanswers);
    let updateParams = {
      label: 'project',
      pk: item.pk,
      id: item.id,
      name: `connections/${connectionIndex}/answers/${id}`,
      value: newanswers[id]
    };
    await updateProperty(updateParams);
  }

  async function updateReferences({ id, newrefs }) {
    let updateParams = {
      label: 'project',
      pk: item.pk,
      id: item.id,
      name: `connections/${connectionIndex}/answers/${id}/references`,
      value: newrefs
    };
    await updateProperty(updateParams);
  }

  const handleSubmit = () => {
    console.log('Questions answers:', answers);
  };

  const printStep = (step, answers) => {
    if (step.noquestions) return '';
    let content = '';
    let answer = answers[step.id] && answers[step.id].note ? answers[step.id].note : '';
    if (step.title) content += `<h2>${step.title}</h2>`;
    if (step.name || answer) content += markdownToHtml(`<b>${step.name || ''}</b> `, answer);
    console.log(content)
    if (answers[step.id] && answers[step.id].references && answers[step.id].references.length > 0) {
      content += `<b>References</b><br/>`;
      content += getReferencesHtml(answers[step.id].references, bibliography);
    }
    if (step.steps) {
      for (let subStep of step.steps) {
        content += printStep(subStep, answers);
      }
    }
    return content;
  };

  const handlePrint = () => {
    if (!currentStep) return;
    const printWindow = window.open('', '_blank');
    let content = `<html><head><title>${currentStep.name ? currentStep.name : 'Print'}</title>
        <style>
          @page { margin: 0 }
          body { font-family: "Times New Roman", Times, serif; font-size: 12pt; margin: 1in; }
        </style>  
        </head>
        <body>`;
    if (currentStep.name) content += `<h1>${currentStep.name}</h1>`;
    if (currentStep.steps) {
      for (let step of currentStep.steps) {
        content += printStep(step, answers);
      }
    }
    content += `</body></html>`;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button onClick={handlePrint} color="primary" variant="contained" size="small">
          Print Answers
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" size="small">
          Save
        </Button>
      </Box>
      {currentStep && currentStep.name && <Typography variant="h5" gutterBottom>{currentStep.name}</Typography>}
      {currentStep && currentStep.description && <Typography variant="body1" gutterBottom>{currentStep.description}</Typography>}
      {currentStep &&
        currentStep.steps &&
        currentStep.steps.map((step, i) => (
          <QStep
            key={i}
            step={step}
            onAnswerChange={handleAnswerChange}
            answers={answers}
            bibliography={bibliography}
            updateReferences={updateReferences}
          />
        ))}
    </Box>
  );
};

export default Questions;
